import { apiClient } from '@/lib/api.ts'

export type MemoryCategory = 'rule' | 'preference' | 'product-knowledge' | 'process'
export type MemoryStatus = 'pending' | 'active' | 'rejected' | 'archived'
export type MemorySource = 'user' | 'ai' | 'admin' | 'edit-diff' | 'document'

export interface DiffContext {
  field: string
  aiBefore: string
  userAfter: string
  positionDescription: string
}

export interface QuoteAiMemory {
  id: string
  content: string
  category: MemoryCategory
  status: MemoryStatus
  source: MemorySource
  createdByUserId: string | null
  triggeredByUserId: string | null
  sourceQuoteId: string | null
  diffContextJson: DiffContext[] | null
  approvedByUserId: string | null
  approvedAt: string | null
  priority: number
  createdAt: string
  updatedAt: string
}

export async function fetchQuoteAiMemory(filters: {
  status?: MemoryStatus
  category?: MemoryCategory
} = {}): Promise<QuoteAiMemory[]> {
  const params: Record<string, string> = {}
  if (filters.status) params['status'] = filters.status
  if (filters.category) params['category'] = filters.category
  const { data } = await apiClient.get<{ data: QuoteAiMemory[] }>('/quote-ai-memory', { params })
  return data.data
}

export async function createQuoteAiMemory(input: {
  content: string
  category?: MemoryCategory
  priority?: number
}): Promise<QuoteAiMemory> {
  const { data } = await apiClient.post<{ data: QuoteAiMemory }>('/quote-ai-memory', input)
  return data.data
}

export async function updateQuoteAiMemory(
  id: string,
  patch: { content?: string; category?: MemoryCategory; status?: MemoryStatus; priority?: number },
): Promise<QuoteAiMemory> {
  const { data } = await apiClient.patch<{ data: QuoteAiMemory }>(`/quote-ai-memory/${id}`, patch)
  return data.data
}

export async function approveQuoteAiMemory(id: string): Promise<QuoteAiMemory> {
  const { data } = await apiClient.post<{ data: QuoteAiMemory }>(`/quote-ai-memory/${id}/approve`)
  return data.data
}

export async function rejectQuoteAiMemory(id: string): Promise<QuoteAiMemory> {
  const { data } = await apiClient.post<{ data: QuoteAiMemory }>(`/quote-ai-memory/${id}/reject`)
  return data.data
}

export async function deleteQuoteAiMemory(id: string): Promise<void> {
  await apiClient.delete(`/quote-ai-memory/${id}`)
}

export type SimilarityLevel = 'exact' | 'overlapping' | 'related'

export interface SimilarMemoryResult {
  id: string
  content: string
  category: MemoryCategory
  similarity: SimilarityLevel
  explanation: string
}

export async function checkSimilarMemories(input: {
  content: string
  category?: MemoryCategory
}): Promise<SimilarMemoryResult[]> {
  const { data } = await apiClient.post<{ data: SimilarMemoryResult[] }>(
    '/quote-ai-memory/check-similar',
    input,
  )
  return data.data
}

export async function fetchSimilarActiveMemories(id: string): Promise<SimilarMemoryResult[]> {
  const { data } = await apiClient.get<{ data: SimilarMemoryResult[] }>(
    `/quote-ai-memory/${id}/similar-active`,
  )
  return data.data
}

export async function mergeQuoteAiMemory(sourceIds: string[]): Promise<QuoteAiMemory> {
  const { data } = await apiClient.post<{ data: QuoteAiMemory }>('/quote-ai-memory/merge', {
    sourceIds,
  })
  return data.data
}
